import { FtueVideoThumbnail } from '../../api/core'

export type VimeoOEmbed = {
  vimeoId: string
  title: string
  durationSeconds: number
  thumbnailUrl: string
}

/** The sizes, in this order, Vimeo gives legacy's picker. Customers read index 2 (295×166) and 3 (640×360). */
export const VIMEO_THUMBNAIL_SIZES: ReadonlyArray<readonly [number, number]> = [
  [100, 75],
  [200, 150],
  [295, 166],
  [640, 360],
  [960, 540],
  [1280, 720],
  [1920, 1080],
]
const PLAY_BUTTON_OVERLAY = 'http://f.vimeocdn.com/p/images/crawler_play.png'

type VimeoThumbnailOptions = {
  /**
   * Keeps the query of the oEmbed URL (`region=us`) in the links, as Vimeo's picker gives them today
   * (`…_100x75?&r=pad&region=us`). Without it the links have the older `…_100x75?r=pad` form.
   */
  keepQuery?: boolean
}

/** Legacy's thumbnail set, built from one oEmbed `thumbnail_url` by swapping the size Vimeo puts at its end. */
export const toVimeoThumbnails = (thumbnailUrl: string, { keepQuery = false }: VimeoThumbnailOptions = {}): FtueVideoThumbnail[] => {
  const [path, query = ''] = thumbnailUrl.split('?')
  const base = path.replace(/_\d+(?:x\d+)?$/, '')
  const kept = keepQuery && query ? query : ''
  return VIMEO_THUMBNAIL_SIZES.map(([width, height]) => {
    const image = `${base}_${width}x${height}`
    return {
      height,
      link: kept ? `${image}?&r=pad&${kept}` : `${image}?r=pad`,
      linkWithPlayButton: `https://i.vimeocdn.com/filter/overlay?src0=${encodeURIComponent(kept ? `${image}?&${kept}` : image)}&src1=${encodeURIComponent(
        PLAY_BUTTON_OVERLAY
      )}`,
      width,
    }
  })
}

/** The Vimeo picture behind a thumbnail link, the same for every size and for Vimeo's old and new URL formats. */
export const getThumbnailPictureId = (link?: string) => link?.match(/i\.vimeocdn\.com\/video\/(\d+)/)?.[1]

/** The play-button variant of a picture link, built the way `toVimeoThumbnails` builds it (without `r=pad`). */
export const toVimeoPlayButtonLink = (link: string) => {
  const [path, query = ''] = link.split('?')
  const rest = query
    .split('&')
    .filter((part) => part !== 'r=pad')
    .join('&')
  const image = rest ? `${path}?${rest}` : path
  return `https://i.vimeocdn.com/filter/overlay?src0=${encodeURIComponent(image)}&src1=${encodeURIComponent(PLAY_BUTTON_OVERLAY)}`
}

export type VimeoPictureSizeResponse = { width: number; height: number; link: string; linkWithPlayButton?: string | null }

export type VimeoPicturesResponse = {
  vimeoId: string
  pictures: { pictureId: string; active: boolean; sizes: VimeoPictureSizeResponse[] }[]
}

export type VimeoPicture = {
  pictureId: string
  // Vimeo's current thumbnail of the video.
  active: boolean
  // Vimeo's sizes in Vimeo's order, stored as they are: customers read `thumbnails[2]` and `thumbnails[3]`.
  thumbnails: FtueVideoThumbnail[]
}

export const toVimeoPictures = (response?: VimeoPicturesResponse): VimeoPicture[] =>
  (response?.pictures ?? [])
    .filter((picture) => picture.sizes?.length)
    .map((picture) => ({
      pictureId: picture.pictureId,
      active: !!picture.active,
      thumbnails: picture.sizes.map(({ width, height, link, linkWithPlayButton }) => ({
        width,
        height,
        link,
        linkWithPlayButton: linkWithPlayButton || toVimeoPlayButtonLink(link),
      })),
    }))

export const isSamePicture = (a: FtueVideoThumbnail[] = [], b: FtueVideoThumbnail[] = []) => {
  const pictureId = getThumbnailPictureId(a[0]?.link)
  return pictureId ? pictureId === getThumbnailPictureId(b[0]?.link) : !!a[0] && a[0].link === b[0]?.link
}

/** The smallest size at least `minWidth` wide, else the largest. */
export const pickThumbnailForWidth = (thumbnails: FtueVideoThumbnail[], minWidth: number) =>
  [...thumbnails].sort((a, b) => a.width - b.width).find((thumbnail) => thumbnail.width >= minWidth) ??
  thumbnails.reduce<FtueVideoThumbnail | undefined>((largest, thumbnail) => (!largest || thumbnail.width > largest.width ? thumbnail : largest), undefined)

/** A Vimeo id, or a vimeo.com / player.vimeo.com link to a video. */
export const parseVimeoId = (input: string): string | undefined => {
  const value = input.trim()
  if (/^\d+$/.test(value)) return value
  return value.match(/^(?:https?:\/\/)?(?:www\.|player\.)?vimeo\.com\/(?:video\/)?(\d+)(?:[/?#].*)?$/)?.[1]
}

export const thumbnailsMatch = (a: FtueVideoThumbnail[] = [], b: FtueVideoThumbnail[] = []) =>
  a.length === b.length &&
  a.every((thumbnail, i) => {
    const other = b[i]
    return (
      thumbnail.link === other.link &&
      thumbnail.linkWithPlayButton === other.linkWithPlayButton &&
      thumbnail.width === other.width &&
      thumbnail.height === other.height
    )
  })
